import { useNavigate, useParams } from 'react-router-dom'

import { Alert, Box, Button, Text } from '@factorialco/deprecated-design-system'
import { isEmpty, omitBy } from 'lodash'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import TextField from 'modules/core/components/TextField'
import { useForm } from 'modules/core/hooks/useForm'
import type { FormErrors } from 'modules/core/hooks/useForm'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { isValidEmail } from 'modules/core/lib/email'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useCreateApplication } from 'modules/ats/hooks/applications/useCreateApplication'

interface NewCandidateFormValues {
  atsJobPostingId: number
  source: string
  firstName: string
  lastName: string
  email?: string
  phone?: string
  personalUrl?: string
}

const JobApplicationsAddCandidatePage = observer(() => {
  const params = useParams()
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const jobId = Number(params.jobId)
  const { create } = useCreateApplication()

  const form = useForm<NewCandidateFormValues>({
    defaultValues: {
      atsJobPostingId: jobId,
      source: 'manually_added',
      email: '',
      firstName: '',
      lastName: '',
      personalUrl: '',
      phone: '',
    },
    validate: (data) => {
      const errors: FormErrors<NewCandidateFormValues> = {}

      if (!data.firstName) {
        errors.firstName = i18n.t('common.empty')
      }

      if (!data.lastName) {
        errors.lastName = i18n.t('common.empty')
      }

      if (data.email && !isValidEmail(data.email, { allowEmpty: true })) {
        errors.email = i18n.t('ats.candidates.candidate_form.invalid_email')
      }

      return errors
    },
    onSubmit: async (data, { setErrors }) => {
      const result = await create({
        ...omitBy(data, isEmpty),
        atsJobPostingId: data.atsJobPostingId,
        source: data.source,
        firstName: data.firstName,
        lastName: data.lastName,
      })

      const handled = handleResult(result, ({ firstName, lastName, email, phone, personalUrl }) => {
        setErrors({
          firstName,
          lastName,
          email,
          phone,
          personalUrl,
        })
      })

      if (handled.success && handled.data.application) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
        navigate(
          navigation.ats.jobs.jobsId.applications.applicationDetail.fullPath({
            jobId,
            application_id: handled.data.application.id,
          })
        )
      }

      return handled
    },
  })

  return (
    <Modal
      illustration="people-add"
      title={i18n.t('ats.candidates.candidate_form.title')}
      description={i18n.t('ats.candidates.candidate_form.description')}
      onClose={() => {
        navigate(navigation.ats.jobs.jobsId.applications.fullPath({ jobId }))
      }}
      onSubmit={() => form.handleSubmit()}
      footer={
        <Button disabled={form.isSubmitting} submit>
          {i18n.t('common.add')}
        </Button>
      }
    >
      <Box gap="s16">
        <TextField
          label={i18n.t('ats.jobs_form.first_name')}
          value={form.values.firstName}
          onChange={(value) => form.setValues({ ...form.values, firstName: value })}
          error={form.touched.firstName ? form.errors.firstName : undefined}
        />
        <TextField
          label={i18n.t('ats.jobs_form.last_name')}
          value={form.values.lastName}
          onChange={(value) => form.setValues({ ...form.values, lastName: value })}
          error={form.touched.lastName ? form.errors.lastName : undefined}
        />
        <TextField
          label={i18n.t('ats.jobs_form.email')}
          value={form.values.email}
          onChange={(value) => form.setValues({ ...form.values, email: value })}
          error={form.touched.email ? form.errors.email : undefined}
        />
        <TextField
          label={i18n.t('ats.jobs_form.personal_url')}
          value={form.values.personalUrl}
          onChange={(value) => form.setValues({ ...form.values, personalUrl: value })}
        />
        <TextField
          label={i18n.t('ats.jobs_form.phone')}
          value={form.values.phone}
          onChange={(value) => form.setValues({ ...form.values, phone: value })}
        />

        <Alert>
          <Text.Normal>
            {i18n.t('ats.candidates.candidate_form.data_protection.message')}{' '}
            <a
              href={i18n.t('ats.candidates.candidate_form.data_protection.url')}
              target="_blank"
              rel="noreferrer"
            >
              {i18n.t('ats.candidates.candidate_form.data_protection.link_text')}
            </a>
          </Text.Normal>
        </Alert>
      </Box>
    </Modal>
  )
})

export default JobApplicationsAddCandidatePage
